import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchContext } from '../context/SearchContext';

export type MapFocusInput = {
  idMaybe?: string | number | null;
  id?: string | number;
  latNum?: number | string | null;
  lngNum?: number | string | null;
  lat?: number | string;
  lng?: number | string;
  lon?: number | string;
  latitude?: number | string;
  longitude?: number | string;
  name?: string;
  address?: string;
  skipModal?: boolean; // Don't open modal, just fly to location
  [key: string]: any;
};

const coalesce = <T,>(...vals: Array<T | null | undefined>) =>
  vals.find(v => v !== null && v !== undefined);

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};

function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) return;
    fn(...args);
    timeout = setTimeout(() => {
      timeout = null;
    }, delay);
  };
}

export function useMapActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setViewMode, setPendingMapFocus } = useSearchContext();

  const focusShopOnMapImmediate = useCallback(
    (input: MapFocusInput) => {
      const idMaybe = coalesce(input.idMaybe, input.id) ?? null;
      const latNum =
        coalesce(
          toNum(input.latNum as any),
        toNum(input.latitude as any),
        toNum(input.lat as any),
      ) ?? null;
    const lngNum =
      coalesce(
        toNum(input.lngNum as any),
        toNum(input.longitude as any),
        toNum(coalesce(input.lng, input.lon) as any),
      ) ?? null;

    const normalized: Record<string, any> = { ...input, skipModal: true };
    if (idMaybe != null) {
      normalized.id = String(idMaybe);
      normalized.idMaybe = String(idMaybe);
    }
    if (latNum !== null) {
      normalized.latNum = latNum;
      normalized.latitude = latNum;
    }
    if (lngNum !== null) {
      normalized.lngNum = lngNum;
      normalized.longitude = lngNum;
    }

    setPendingMapFocus(normalized as any);

    try {
      sessionStorage.setItem('gr:pending-map-focus', JSON.stringify(normalized));
    } catch {}

    if (location.pathname !== '/') {
      navigate('/');
    }
    setViewMode('map');
    },
    [location, navigate, setPendingMapFocus, setViewMode],
  );

  const focusShopOnMap = useMemo(
    () => debounce(focusShopOnMapImmediate, 250),
    [focusShopOnMapImmediate],
  );

  const openOnMap = focusShopOnMap;
  return { openOnMap, focusShopOnMap };
}

export default useMapActions;
